/**
 * URL modifier helper: binds route parameters (e.g. :id) to URL paths.
 * Path params object is typed as Record<string, string | number> for React route compatibility.
 */
const UrlModifier = {
  _bind(
    target: string | null | undefined,
    itemKey: string,
    value: string | number
  ): string | null | undefined {
    if (!target || typeof target !== 'string') {
      return target;
    }
    const replace = ':' + itemKey;
    const re = new RegExp(replace, 'g');
    return target.replace(re, String(value));
  },

  /**
   * Binds parameters to URL by replacing :param placeholders with values.
   *
   * @param url - The URL template (e.g. "/events/:id/markets")
   * @param params - Key-value map of param names to values
   * @returns URL with placeholders replaced
   */
  bindToUrl(
    url: string | null | undefined,
    params: Record<string, string | number>
  ): string | null | undefined {
    if (!url || typeof url !== 'string') {
      return url;
    }
    let result = url;
    const entries = Object.entries(params);
    for (const [name, key] of entries) {
      result = this._bind(result, name, key) ?? result;
    }
    return result;
  },

  replaceAll(str: string, mapObj: Record<string, unknown>): string {
    const newMapObj: Record<string, unknown> = {};
    const keys = Object.entries(mapObj);
    for (const [name] of keys) {
      newMapObj[':' + name] = keys;
    }

    const re = new RegExp(Object.keys(newMapObj).join('|'), 'gi');
    return str.replace(re, function (matched) {
      return (newMapObj[matched.toLowerCase()] as string) ?? matched;
    });
  },

  /**
   * Parses query string from URL into an object.
   *
   * @param url - URL containing ?key=value&...
   * @returns Object with decoded query params
   */
  paramsToObject(url: string): Record<string, string> {
    const params: Record<string, string> = {};
    if (!url.includes('?')) {
      return params;
    }

    const search = url.split('?')[1];
    return JSON.parse(
      '{"' +
        search.replace(/&/g, '","').replace(/=/g, '":"') +
        '"}',
      function (key: string, value: string) {
        return key === '' ? value : decodeURIComponent(value);
      }
    );
  },
};

export default UrlModifier;
